import { MediaMatcher } from '@angular/cdk/layout';
import { APP_BASE_HREF, DOCUMENT, DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginLibService } from '@ebootis/login';
import { MessageService } from '@ebootis/utilities';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, Subject, iif, of } from 'rxjs';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ArticleService } from '../../modules/article/article.service';
import { PrintService } from '../../modules/print/print.service';
import { LogisticAreaService } from '../../modules/shared/logistic-area/logistic-area.service';
import { Configuration } from '../../services/Ebootis.Queries/Configuration';
import { InfoText } from '../../services/Ebootis.Queries/InfoText';
import { LobByOid } from '../../services/Ebootis.Queries/LobByOid';
import { IZbsApp } from '../../services/Ebootis.ServiceProxies/IZbsApp';
import { ArticleBasketService } from '../../services/article-basket.service';
import { StorageConfigService } from '../../services/storage-config.service';
import { ConfigurationService, HelpDocument } from '../configuration-select/configuration.service';
import { StyleService } from '../configuration-select/style.service';
import { InfoDialogComponent, InfoDialogInput } from '../info-dialog/info-dialog.component';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy {

	public readonly CONFIG_OID: string = 'configOid';

	@ViewChild('sidenav') public sidenav: MatSidenav;

	private _mobileQueryListener: () => void;
	private m_subscriptionsSubject = new Subject();
	private m_loggedIn: boolean = false;
	public m_isPrinting: boolean = false;
	public m_toolbarImageSrc$: Observable<string> = this.styleService.appLogoSrc;
	public get Cfg(): Configuration.Result {
		return this.m_cfg;
	}
	private m_cfg: Configuration.Result;
	public m_mobileQuery: MediaQueryList;
	public m_opened: boolean = true;
	public m_helpDoc: HelpDocument = null;

	constructor(
		@Inject(APP_BASE_HREF) private baseHref: string,
		private m_logisticAreaService: LogisticAreaService,
		private m_changeDetectorRef: ChangeDetectorRef,
		private m_router: Router,
		private m_mediaMatcher: MediaMatcher,
		private m_lobByOid: LobByOid,
		public m_translateService: TranslateService,
		public m_loginLibService: LoginLibService,
		public m_configurationService: ConfigurationService,
		public m_articleService: ArticleService,
		public m_basketService: ArticleBasketService,
		private styleService: StyleService,
		private m_printService: PrintService,
		private storageConfig: StorageConfigService,
		private routerState: ActivatedRoute,
		private m_zbsService: IZbsApp,
		private messageService: MessageService,
		private m_datePipe: DatePipe,
		private m_dialog: MatDialog,
		private m_infoTextQuery: InfoText,
		@Inject(DOCUMENT) private document: Document
	) {
		this.m_mobileQuery = this.m_mediaMatcher.matchMedia('(min-width: 1200px)')
		// Während eines Druckvorgangs soll nur das druckbereite Router-Outlet,
		// nicht das "normale" abgebildet werden
		this.m_printService.isPrinting.subscribe({
			next: (isPrinting) => {
				this.m_isPrinting = isPrinting;
			}
		})
		this._mobileQueryListener = () => this.m_changeDetectorRef.detectChanges();
	}

	/**
	 * Die verwenddete Sprache sowie Defaultsprache des TranslateService wird gesetzt. Die Konfiguration wird geladen
	 * und ein Eventlistener gesetzt welcher den Loginstatus überprüft.
	 */
	public ngOnInit(): void {
		this.m_translateService.setDefaultLang('de');
		// remove the ending slash and get  the two letter lang
		const language: string = this.baseHref.substr(this.baseHref.length - 3, 2);
		this.m_translateService.use(language);
		this.document.documentElement.lang = language;

		this.m_loginLibService.loggedInObservable
			.subscribe({
				next: (loggedIn) => {

					if (!loggedIn) {
						if (this.m_loggedIn) // wenn man vorher angemeldet war
							this.resetSession();
						this.m_loggedIn = false;
					} else {
						this.m_loggedIn = true;

						this.storageConfig.setPrefix(this.m_loginLibService.LoggedInUser);
						this.m_zbsService.canUserOrder().subscribe({
							next: (date: Date) => {
								if (date) {
									const parm = this.m_datePipe.transform(date, 'shortDate', null, this.m_translateService.currentLang);
									this.messageService.info(this.m_translateService.instant('ORDER.USER_OFFSET.TEXT', { date: parm }));
								}
							}
						});

						this.m_infoTextQuery.query().pipe(take(1)).pipe(switchMap((bodyText) => {
							if (bodyText)
								return this.m_dialog.open<InfoDialogComponent, InfoDialogInput>(InfoDialogComponent, {
									data: {
										body: bodyText
									}
								}).afterClosed();
							else return of(null);
						})).subscribe();
					}

				}
			});

		this.m_configurationService.getConfiguration()
			.pipe(takeUntil(this.m_subscriptionsSubject))
			.pipe(tap((cfg) => {
				if (cfg) {
					this.m_cfg = cfg;
					if (this.m_cfg.logo != null) {
						this.styleService.setAppLogoSrc(this.m_lobByOid.getLobUrl({ lob: this.m_cfg.logo.oid }));
					}
					if (this.m_cfg.stylesheet != null) {
						this.styleService.createCustomStyleSheetElement(this.m_lobByOid.getLobUrl({ lob: this.m_cfg.stylesheet.oid }), this.styleService.CUSTOM_STYLESHEET_ID);
					}
					// neuladen, wenn die app wieder im Vordergrund ist
					document.addEventListener(
						'visibilitychange', (ev) => {
							if (this.m_cfg != null && this.m_cfg.oid != this.m_configurationService.SelectedConfigOid) {
								location.reload();
							}
						});
				} else {
					this.styleService.removeCustomStyleSheet(this.styleService.CUSTOM_STYLESHEET_ID);
				}
			}))
			.pipe(switchMap((cfg) => iif(() => cfg != null, this.m_configurationService.getHelpDocument(), EMPTY)))
			.pipe(takeUntil(this.m_subscriptionsSubject)).subscribe({
				next: (result) => {
					this.m_helpDoc = result;
				}
			});
	}

	/**
	 * Die Subscriptions werden geschlossen.
	 */
	public ngOnDestroy(): void {
		this.m_mobileQuery.removeListener(this._mobileQueryListener);
		this.m_subscriptionsSubject.next(null);
		this.m_subscriptionsSubject.complete();
	}

	/**
	 * Logout wird ausgeführt und Methode resetSession aufgerufen.
	 */
	public logout_Click(): void {
		this.m_loginLibService.logout().pipe(
			takeUntil(this.m_subscriptionsSubject),
		).subscribe({
			next: (res) => {
				this.resetSession();
			},
		});
	}

	/**
	 * Sidenav wird auf Ursprungszustand gesetzt. Es wird auf die Anmeldeseite gewechselt.
	 */
	private resetSession(): void {
		this.m_logisticAreaService.removeLogisticArea(); // das gehört hier nicht hin
		this.m_configurationService.removeConfigSelected(); // das auch nicht! loginService für diese App!
		this.storageConfig.setPrefix('');
		if (this.sidenav) {
			if (this.m_mobileQuery.matches) {
				this.sidenav.opened = true;
			} else {
				this.sidenav.opened = false;
			}
		}
		this.m_router.navigate(['./anmelden']);
	}

	/**
	 * Setzt die Ausgewählte Sprache im TranslateService.
	 * @param p_lang Ausgewählte Sprache (ISO-Code)
	 */
	public selectLang_Click(p_lang: string): void {
		//nicht so schön!!! TODO
		const baseHref: string = this.baseHref.substr(0, this.baseHref.length - 3) + p_lang + '/';
		location.href = location.href.replace(this.baseHref, baseHref);
	}

	/**
	 * Öffnet die Übersichtsseite bei Klick auf das Logo falls eingeloggt und Konfiguration ausgewählt.
	 * @param p_loggedIn Loginstatus aus dem LoginLibService
	 * @param p_configOid Konfiguration aus dem ConfigurationService
	 */
	public openDashboard_Click(p_loggedIn, p_configOid): void {
		if (p_loggedIn) {
			if (p_configOid) {
				this.m_router.navigate(['./uebersicht']);
			}
		}
	}
}
